import { IAlertProps } from '../../components/Page/Page';
import { Product, ProductImage, ProductImageItemInput, ProductInfo, ProductOption, ProductOptionInput, ProductReviewStatus, ProductShippingSetting, ProductStatus, ProductType, ProductTypeOptions, ProductUserContext, ProductVariantInput, ProductVariantOption, ProductVariantOptionInput, ShippingCountryOfOriginOption, StandardShipping } from '@shopify-multiseller/graphql/partner/types';

export const updateProductVariantsWithOptionValues = (
  variants: ProductVariantInput[],
  options?: ProductOptionInput[]
): ProductVariantInput[] => {
  if (!options || options.length === 0) {
    const variant = { ...variants[0], variantOptions: undefined };
    return [variant];
  }

  const updatedVariants: ProductVariantInput[] = [];

  variants.forEach((variant) => {
    const variantOptionsMap = new Map<string, string>();

    variant.variantOptions?.forEach((vOption) => {
      const matchingOption = options.find((opt) => opt.id === vOption.optionId);
      if (
        matchingOption &&
        matchingOption.values.includes(vOption.value) &&
        !variantOptionsMap.has(vOption.optionId)
      ) {
        variantOptionsMap.set(vOption.optionId, vOption.value);
      }
    });

    options.forEach((option) => {
      if (!variantOptionsMap.has(option.id)) {
        variantOptionsMap.set(option.id, option.values[0]);
      }
    });

    const variantOptionsArray = Array.from(
      variantOptionsMap,
      ([optionId, value]) => ({
        optionId,
        value,
      })
    );

    const updatedVariant = { ...variant, variantOptions: variantOptionsArray };

    if (!updatedVariants.some((v) => v.id === updatedVariant.id)) {
      updatedVariants.push(updatedVariant);
    }
  });

  return updatedVariants;
};

/**
 * Every variant with the form's track-quantity setting applied.
 *
 * Returns new variant objects rather than writing the flag onto the ones it was
 * given. Formik starts with `values` and `initialValues` sharing their nested
 * objects, so mutating a variant in place changed both at once — the edit was
 * made and simultaneously became the thing it is compared against, leaving the
 * form certain nothing had happened.
 */
export const updateProductVariantsWithTrackQuantityValues = (
  variants: ProductVariantInput[],
  trackQuantity: boolean
): ProductVariantInput[] => variants.map((variant) => ({ ...variant, trackQuantity }));

export const resolveOptions = (
  options?: ProductOption[]
): ProductOptionInput[] | undefined => {
  if (!options || options.length === 0) return undefined;
  return options.map((option) => ({
    id: option.id,
    name: option.name,
    values: option.values,
    position: option.position,
  }));
};

export const resolveImages = (
  images?: ProductImage[]
): ProductImageItemInput[] | undefined => {
  if (!images || images.length === 0) return undefined;
  return images.map((image) => ({
    id: image.id,
    isFeatured: image.isFeatured,
    order: image.order,
  }));
};

export const resolveVariantOptions = (
  options?: ProductVariantOption[]
): ProductVariantOptionInput[] | undefined => {
  if (!options || options.length === 0) return undefined;
  return options.map((option) => ({
    optionId: option.optionId,
    value: option.value,
  }));
};

/**
 * Which parts of a product the seller has edited but the shop has not yet
 * approved — "the updated title and description".
 *
 * `current` is what is live and `pending` is what is waiting, and the query
 * already asks for both, so this is a comparison rather than a request. Naming
 * the fields is the difference between a notice a seller has to investigate
 * and one they can act on: told only that "changes" are under review, the only
 * way to find out which is to remember what you typed.
 *
 * Prices and stock live on variations, so a change to either reads as "pricing
 * and stock" rather than as a list of variation names nobody set out to edit.
 */
const changedParts = (product: Product): string[] => {
  const live = product.current;
  const waiting = product.pending;
  if (!live || !waiting) return [];

  const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const parts: string[] = [];

  if (!same(live.title, waiting.title)) parts.push('title');
  if (!same(live.description, waiting.description)) parts.push('description');
  if (!same(live.images, waiting.images)) parts.push('images');
  if (!same(live.variants, waiting.variants) || !same(live.options, waiting.options)) {
    parts.push('pricing and stock');
  }
  if (
    !same(live.standardShipping, waiting.standardShipping) ||
    !same(live.deliveryProfile, waiting.deliveryProfile)
  ) {
    parts.push('shipping');
  }
  return parts;
};

/** "the title and description", "the title, images and shipping". */
const listParts = (parts: string[]): string | undefined => {
  if (parts.length === 0) return undefined;
  if (parts.length === 1) return `the updated ${parts[0]}`;
  return `the updated ${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

/**
 * The state-of-this-product notices, shown as page alerts under the heading.
 *
 * These used to be full-bleed coloured bars pinned above the header, which
 * shouted the same message however routine it was — "this product is offline"
 * got the same weight as "your changes were rejected". As alerts they keep
 * their status but sit in the page's own reading order.
 */
export const generateProductAlerts = (
  product: Product
): IAlertProps[] => {
  const alerts: IAlertProps[] = [];

  if (product.isDetatched) {
    alerts.push({
      status: 'warning',
      message: 'This product has been disabled and removed from the store.',
    });
  }

  if (product.status === ProductStatus.Archived) {
    alerts.push({
      status: 'info',
      message:
        'This product has been archived. To return this product to Draft, please click "Restore".',
    });
    return alerts;
  }

  if (product.reviewStatus === ProductReviewStatus.ChangesPending) {
    alerts.push({
      status: 'warning',
      // The fact this screen most needs to state is the one it left out: the
      // product is still live and still selling on the old version. Without
      // that, "changes are being reviewed" reads as though the listing had
      // been pulled while somebody looks at it.
      title: 'Your latest changes are waiting for review',
      message: `The shop is reviewing ${
        listParts(changedParts(product)) ?? 'them'
      }. The previous version stays live meanwhile.`,
    });
  }
  if (product.reviewStatus === ProductReviewStatus.Pending) {
    alerts.push({
      status: 'warning',
      message: 'This product is waiting to be reviewed by an administrator.',
    });
  }
  if (product.reviewStatus === ProductReviewStatus.Rejected) {
    alerts.push({
      status: 'error',
      message: `This product has been rejected. Reason: ${product.rejectedReason}`,
    });
  }
  if (product.reviewStatus === ProductReviewStatus.ChangesRejected) {
    alerts.push({
      status: 'error',
      message: `Changes to this product have been rejected. Reason: ${product.rejectedReason}`,
    });
  }

  if (product.updatedBy === ProductUserContext.Shop) {
    alerts.push({
      status: 'info',
      message: 'This product has been updated by the store.',
    });
  }
  if (product.status === ProductStatus.Draft) {
    alerts.push({
      status: 'info',
      message: 'This product is offline and will not be visible in the store. ',
    });
  }

  return alerts;
};

export const resolveShippingType = (
  product: ProductInfo
): 'profile' | 'standard' => {
  if (product.deliveryProfile?.id) {
    return 'profile';
  }
  return 'standard';
};

export const resolveInitialProductType = (
  productTypeOptions: ProductTypeOptions
): ProductType | undefined => {
  if (productTypeOptions === ProductTypeOptions.Both) {
    return undefined;
  }
  return productTypeOptions === ProductTypeOptions.Physical
    ? ProductType.Physical
    : ProductType.Digital;
};

export const resolveInitialStandardShipping = (
  productShipping: ProductShippingSetting,
  shopCountryCode?: string,
  sellerCountryCode?: string
): StandardShipping | undefined => {
  let countryOfOrigin = shopCountryCode;
  if (
    productShipping.shippingCountryOfOriginOption ===
    ShippingCountryOfOriginOption.SellerCountryOnly
  ) {
    countryOfOrigin = sellerCountryCode;
  }
  return {
    countryOfOrigin: countryOfOrigin,
    domesticRate: 0,
    internationalRate: 0,
  };
};

export const resolveInitialTrackQuantity = (
  productTypeOptions: ProductTypeOptions
): boolean => {
  if (productTypeOptions === ProductTypeOptions.Both) {
    return true;
  }
  return productTypeOptions === ProductTypeOptions.Physical
    ? true
    : false;
};

// export const resolveInitialShippingType = (productShipping: ProductShippingSetting): 'standard' | undefined => {
//   if (productShipping.allowProfiles) {
//     return  undefined;
//   }
//   return 'standard';
// };

/**
 * Recursively removes all __typename fields from an object
 * This is needed when passing Apollo Client cached data to GraphQL mutations
 * as __typename is metadata and not part of the input types
 */
export const removeTypenames = <T>(obj: T): T => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => removeTypenames(item)) as T;
  }

  if (typeof obj === 'object') {
    const newObj: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key !== '__typename') {
        newObj[key] = removeTypenames(value);
      }
    }
    return newObj as T;
  }

  return obj;
};
